// @flow
import classnames from 'classnames';
import { isEmpty } from 'lodash';
import React, { Children, cloneElement, type Node, useContext } from 'react';
import { Collapse, UnmountClosed } from 'react-collapse';

import { ActiveIndexes } from 'Components/frameworks/active-indexes/ActiveIndexes';
import { type IconName } from 'Components/frameworks/icon/Icon';
import { SiteContainer } from 'Components/frameworks/site-container/SiteContainer';
import { TabHeader } from 'Components/frameworks/tab-header/TabHeader';
import ResponsiveContext from 'Components/utils/responsive/responsiveContext';
import { scrollToElement } from 'Components/utils/scroll/scrollToElement';
import { withOpenAndScrollToHash } from 'Components/utils/tabs/tabs.hoc';
import { getTabsBackgroundClasses } from 'Components/utils/tabs/tabs.util';
import { NAVIGATION_OFFSET_SELECTOR } from 'Components/widgets/navigation/Navigation.constants';
import { ARROW_DOWN, ARROW_UP, END, HOME } from 'Utils/keyCodes';

type AccordionItem = {
  header: {
    color?: Colors,
    iconName?: IconName,
    title: Node,
  },
  headerId: string,
  panelId: string,
  content: Node,
};

type AccordionProps = {
  areAllCollapsable?: boolean,
  areMultiExpandable?: boolean,
  className?: string,
  collapseIconName?: IconName,
  containerSize?: 'mini' | 'small' | 'medium' | 'large',
  expandIconName?: IconName,
  hasBackground?: boolean,
  hasNoAutoScroll?: boolean,
  headerLevel?: number,
  id?: string,
  initialSelectedItems?: Array<number>,
  isContentUnmountedOnCollapse?: boolean,
  items: Array<AccordionItem>,
  onSelect?: (index: number) => void,
  scrollOffsetSelector?: string,
  theme?: string,
};

function scrollToId(id: string, offsetSelector: string) {
  scrollToElement(`#${id}`, 100, `.${offsetSelector}`);
}

function _Accordion(props: AccordionProps) {
  const {
    areAllCollapsable,
    areMultiExpandable,
    className,
    collapseIconName,
    containerSize,
    expandIconName,
    hasBackground,
    hasNoAutoScroll,
    headerLevel = 3,
    initialSelectedItems = [],
    id,
    isContentUnmountedOnCollapse,
    items,
    onSelect,
    scrollOffsetSelector = NAVIGATION_OFFSET_SELECTOR,
    theme = 'default',
  } = props;

  const { isTablet } = useContext(ResponsiveContext);

  if (isEmpty(items)) return null;

  const refsArray = [];

  const onKeyDown = ({ e, index }) => {
    if (items.length > 1) {
      const lastItem = items.length - 1;

      let idx = -1;

      switch (e.which) {
        case ARROW_DOWN:
          e.preventDefault();
          idx = index === lastItem ? 0 : index + 1;
          break;
        case ARROW_UP:
          e.preventDefault();
          idx = index === 0 ? lastItem : index - 1;
          break;
        case HOME:
          e.preventDefault();
          idx = 0;
          break;
        case END:
          e.preventDefault();
          idx = lastItem;
          break;
        default:
      }

      refsArray[idx] && refsArray[idx].focus();
    }
  };

  const CollapsedTag = isContentUnmountedOnCollapse ? UnmountClosed : Collapse;

  const accordionsCount = items.filter(Boolean).length;

  return (
    <ActiveIndexes
      activeIndexes={initialSelectedItems}
      hasMultipleIndexEnabled={areMultiExpandable}
      hasNoIndexEnabled={areAllCollapsable}
      onUpdate={onSelect}
    >
      {({ update, activeIndexes, isPristine }) => (
        <section
          className={className}
          id={id}
          data-cs-override-id="Accordion" // ContentSquare must ignore this id see https://docs.contentsquare.com/uxa-fr/#ignorer-remplacer-ou-ajouter-un-id
        >
          {items.filter(Boolean).map(({ content, header = {}, headerId, panelId }, index) => {
            const { title, ...rest } = header;

            const isExpanded = activeIndexes.includes(index);

            const onRest = () => {
              if (!hasNoAutoScroll && isExpanded && !isPristine)
                scrollToId(headerId, scrollOffsetSelector);
            };

            const Heading = `h${headerLevel}`;

            return (
              <article key={headerId}>
                <SiteContainer size={containerSize}>
                  <Heading className="reset-heading">
                    <TabHeader
                      {...rest}
                      collapseIconName={collapseIconName}
                      controlsId={panelId}
                      expandIconName={expandIconName}
                      id={headerId}
                      isAccordion
                      isActive={isExpanded}
                      isDisabled={accordionsCount <= 1 && isTablet}
                      onClick={() => {
                        update(index);
                        refsArray[index] && refsArray[index].focus();
                      }}
                      onKeyDown={e => onKeyDown({ e, index })}
                      ref={ref => {
                        refsArray[index] = ref;
                      }}
                      theme={theme}
                    >
                      {title}
                    </TabHeader>
                  </Heading>
                </SiteContainer>
                <CollapsedTag isOpened={isExpanded} onRest={onRest}>
                  <div
                    aria-hidden={!isExpanded}
                    aria-labelledby={headerId}
                    className={classnames(getTabsBackgroundClasses(hasBackground), {
                      'invisible pointer-events-none': !isExpanded,
                    })}
                    data-cs-override-id="Accordion-panel" // ContentSquare must ignore this id see https://docs.contentsquare.com/uxa-fr/#ignorer-remplacer-ou-ajouter-un-id
                    id={panelId}
                    role="region"
                  >
                    <SiteContainer size={containerSize}>
                      {Children.map(
                        content,
                        (el, i) => (el ? cloneElement(el, { key: `${panelId}-${i}` }) : null),
                      )}
                    </SiteContainer>
                  </div>
                </CollapsedTag>
              </article>
            );
          })}
        </section>
      )}
    </ActiveIndexes>
  );
}

export const Accordion = withOpenAndScrollToHash(_Accordion);
